import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { builderSessionId, useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { validateBuilderMetadata } from "../../lib/metadata-builder/validate-metadata";
import { downloadBuilderMetadata } from "../../lib/metadata-builder/download-metadata";
import { metadataToJson } from "../../lib/schedule-presets/metadata-json";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { ReviewSummary } from "./ReviewSummary";
import { previewModesFor } from "../../lib/metadata-builder/preview-modes";
import { ReviewValidation } from "./ReviewValidation";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export function ReviewStep() {
  const { t } = useTranslation();
  const session = useMetadataHistoryStore(state => state.sessions[builderSessionId]);
  const uploading = useMetadataBuilderStore(state => state.uploading);
  const uploadIssues = useMetadataBuilderStore(state => state.uploadIssues);
  const [downloadedJson, setDownloadedJson] = useState<string | null>(null);
  const metadata = session?.projection;
  const artwork = session?.artwork;
  const issues = useMemo(() => {
    const issues = metadata ? validateBuilderMetadata(metadata, artwork) : [];
    const artworkBlocked = uploading || uploadIssues.length > 0;
    if (artworkBlocked && !issues.some(issue => issue.code === "artwork")) issues.push({ code: "artwork", path: "artwork", step: "upload" });
    return issues;
  }, [metadata, artwork, uploading, uploadIssues]);
  const json = useMemo(() => metadata ? metadataToJson(metadata) : "", [metadata]);
  useEffect(() => {
    endMetadataMerge();
    useMetadataBuilderStore.setState({ previewModes: previewModesFor("mixed"), previewDay: null });
    useMetadataEditingStore.getState().select(null);
  }, []);
  if (!metadata) return null;
  const blocked = issues.length > 0 || uploading || uploadIssues.length > 0;
  return <div className="space-y-4">
    <ReviewSummary metadata={metadata} />
    <ReviewValidation metadata={metadata} issues={issues} />
    <label className="grid gap-1 text-xs font-medium">
      {t("metadata.jsonTab")}
      <Textarea value={json} readOnly spellCheck={false} wrap="off" className="h-64 resize-y font-mono text-xs" />
    </label>
    <div className="space-y-2">
      <Button variant="outline" size="sm" disabled={blocked} onClick={() => {
        endMetadataMerge();
        if (downloadBuilderMetadata(metadata, artwork)) setDownloadedJson(json);
      }}><Download />{t("builder.review.download")}</Button>
      {downloadedJson === json && !blocked && <p className="text-green-800">{t("builder.review.downloaded")}</p>}
    </div>
    <div className="space-y-2 rounded border border-neutral-200 p-3 text-neutral-600">
      <h4 className="font-semibold">{t("builder.review.handoff")}</h4>
      <p>{t("builder.review.handoffHelp")}</p>
      <p>{t("builder.review.normalizeHelp")}</p>
      <p>{t("builder.review.editorHelp")}</p>
      <p>{t("builder.review.fontHelp")}</p>
    </div>
  </div>;
}
